import type { OutcomeKind } from '@unipostudio/platform-constants';
import { api } from './api';

export interface ReportCounts {
  conversations: number;
  enquiries: number;
  bookings: number;
  orders: number;
  sales: number;
  /** Sales and orders in the billing currency, minor units. */
  value: number;
}

export interface ReportTotals {
  conversations: number;
  enquiries: number;
  bookings: number;
  orders: number;
  sales: number;
  salesValue: number;
  currency: string;
  otherCurrencies: Array<{ currency: string; value: number }>;
  medianPersonReplyMinutes: number | null;
  medianAgentReplyMinutes: number | null;
}

export interface PostReport {
  postId: string;
  platforms: string[];
  publishedAt: string | null;
  caption: string;
  thumbnailUrl: string | null;
  reach: number;
  confirmed: ReportCounts;
  likely: ReportCounts;
}

/** Customers from social (Task 3.3). */
export interface CustomersReport {
  enabled: true;
  from: string;
  to: string;
  totals: ReportTotals & { unanswered: number };
  /** null when the period before reaches past what the plan keeps. */
  previous: ReportTotals | null;
  /** views: the period's views (null when no channel reports them); reach: its old name. */
  funnel: { views?: number | null; reach: number; conversations: number; enquiries: number; customers: number };
  posts: PostReport[];
  topics: Array<{ id: string; label: string; count: number }>;
}

export interface PostConversation {
  key: string;
  platform: string;
  peer: string | null;
  startedAt: string;
  confidence: 'confirmed' | 'likely';
  outcomes: OutcomeKind[];
}

/** The inbox's outcome filter: `conversation` for those started in the period. */
export type ReportFilterKind = OutcomeKind | 'conversation';

/** /api/v1/reports/customers (docs/features/customers-from-social.md). */
export const reportsService = {
  async customers(days: number): Promise<CustomersReport | { enabled: false }> {
    // Read by Overview, Inbox and Analytics on every visit: a plan or Starter
    // refusal is not the visitor's action, so no dialog or toast opens for it.
    return (await api.get<CustomersReport | { enabled: false }>('/reports/customers', { params: { days }, handlesPlanErrors: true })).data;
  },
  async conversationKeys(kind: ReportFilterKind, days: number): Promise<string[]> {
    return (await api.get<{ keys: string[] }>('/reports/customers/conversations', { params: { kind, days } })).data.keys;
  },
  async postCounts(ids: string[]): Promise<Record<string, { confirmed: ReportCounts; likely: ReportCounts }>> {
    if (ids.length === 0) return {};
    return (await api.get<Record<string, { confirmed: ReportCounts; likely: ReportCounts }>>('/reports/customers/posts', { params: { ids: ids.join(',') } })).data;
  },
  async postConversations(postId: string): Promise<PostConversation[]> {
    return (await api.get<PostConversation[]>(`/reports/customers/posts/${postId}/conversations`)).data;
  },
};

/** A scheduled client report (GET /reports/schedules, Agency). */
export interface ReportSchedule {
  id: string;
  cadence: 'weekly' | 'monthly';
  recipients: string[];
  language: string;
  enabled: boolean;
  nextRunAt: string;
  lastSentAt: string | null;
  /** sent | not_configured (no email provider: nothing was sent) | failed */
  lastStatus: string | null;
}

export const reportSchedulesService = {
  async list(): Promise<ReportSchedule[]> {
    const res = await api.get('/reports/schedules', { handlesPlanErrors: true });
    return Array.isArray(res.data) ? res.data : [];
  },
  async create(dto: { cadence: 'weekly' | 'monthly'; recipients: string[]; language: string }): Promise<ReportSchedule> {
    const res = await api.post('/reports/schedules', dto);
    return res.data;
  },
  async update(id: string, dto: Partial<Pick<ReportSchedule, 'cadence' | 'recipients' | 'language' | 'enabled'>>): Promise<ReportSchedule> {
    const res = await api.patch(`/reports/schedules/${encodeURIComponent(id)}`, dto);
    return res.data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/reports/schedules/${encodeURIComponent(id)}`);
  },
};
